<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    default: '',
    required: false,
  },
  isInboxView: {
    type: Boolean,
    default: false,
  },
  isContactPanelOpen: {
    type: Boolean,
    default: true,
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const { t } = useI18n();

const activeIndex = ref(0);

const currentChat = computed(() => store.getters.getSelectedChat);
const dashboardApps = computed(() => store.getters['dashboardApps/getRecords']);

const dashboardAppTabs = computed(() => {
  return [
    {
      key: 'messages',
      index: 0,
      name: t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
    },
    ...dashboardApps.value.map((dashboardApp, index) => ({
      key: `dashboard-${dashboardApp.id}`,
      index: index + 1,
      name: dashboardApp.title,
    })),
  ];
});

// eslint-disable-next-line no-unused-vars
const showContactPanel = computed(() => {
  return props.isContactPanelOpen && currentChat.value.id;
});

const fetchLabels = () => {
  if (!currentChat.value.id) {
    return;
  }
  store.dispatch('conversationLabels/get', currentChat.value.id);
};

const onDashboardAppTabChange = index => {
  activeIndex.value = index;
};

watch(
  () => currentChat.value.inbox_id,
  inboxId => {
    if (inboxId) {
      store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
    }
  },
  { immediate: true }
);

watch(
  () => currentChat.value.id,
  () => {
    fetchLabels();
    activeIndex.value = 0;
  }
);

onMounted(() => {
  fetchLabels();
  store.dispatch('dashboardApps/get');
});
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-transparent relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
